import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { FixedBidResponse, NotificationResponse, UserModel } from '../interfaces/responses/models';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {
  private cachedNotifications: NotificationResponse[] | null = null;
  private cachedUnreadNotification: boolean | null = null;
  private isWeb: boolean;
  private project_prefix = environment.project_prefix;

  private _user$ = new BehaviorSubject<any>(this._readUserObjectSync());
  public user$ = this._user$.asObservable();

  constructor() {
    this.isWeb = Capacitor.getPlatform() === 'web';
  }

  // --- Internal Generic Methods ---

  private async _set(key: string, value: string): Promise<void> {
    const fullKey = this.project_prefix + key;
    if (this.isWeb) {
      localStorage.setItem(fullKey, value);
    } else {
      await Preferences.set({ key: fullKey, value });
    }
  }

  private async _get(key: string): Promise<string | null> {
    const fullKey = this.project_prefix + key;
    if (this.isWeb) {
      const val = localStorage.getItem(fullKey);
      return (val === 'undefined' || val === null) ? null : val;
    } else {
      const { value } = await Preferences.get({ key: fullKey });
      return (value === 'undefined' || value === null) ? null : value;
    }
  }

  private _getSync(key: string): string | null {
    const fullKey = this.project_prefix + key;
    const val = localStorage.getItem(fullKey);
    return (val === 'undefined' || val === null) ? null : val;
  }

  private _setSync(key: string, value: string): void {
    const fullKey = this.project_prefix + key;
    localStorage.setItem(fullKey, value);
  }

  // --- Public API ---

  getUser(): string {
    return this._getSync('hygge_user') || '';
  }

  setUser(user: string): void {
    this._setSync('hygge_user', user);
    try {
      const parsed = this._safeParse(user);
      this._user$.next(parsed ?? user);
    } catch { /* ignore */ }
  }

  async getToken(): Promise<string> {
    return (await this._get('token')) || '';
  }

  async setToken(token: string): Promise<void> {
    await this._set('token', token);
    const cur = this._readUserObjectSync();
    if (cur && typeof cur === 'object') {
      this._user$.next({ ...cur, session_token: token });
    }
  }

  async setNotifications(notifications: NotificationResponse[]): Promise<void> {
    this.cachedNotifications = notifications;
    await this._set('notification', JSON.stringify(notifications));
  }

  async getNotifications(): Promise<NotificationResponse[]> {
    if (this.cachedNotifications) return this.cachedNotifications;
    const raw = await this._get('notification');
    this.cachedNotifications = raw ? JSON.parse(raw) : [];
    return this.cachedNotifications || [];
  }

  setPhoneNumber(phone_number: any): void {
    this._setSync('phone_number', phone_number);
  }

  getPhoneNumber(): string {
    return this._getSync('phone_number') || '';
  }

  setRole(role: any): void {
    this._setSync('role', role);
    const cur = this._readUserObjectSync();
    if (cur && typeof cur === 'object') {
      this._user$.next({ ...cur, role });
    }
  }

  getRole(): string {
    return this._getSync('role') || '';
  }

  setUserDetail(user: UserModel): void {
    this._setSync('userDetail', JSON.stringify(user));
  }

  getUserDetail(): UserModel | null {
    const raw = this._getSync('userDetail');
    return raw ? JSON.parse(raw) : null;
  }

  getUserId(): string {
    return this._getSync('userId') || '';
  }

  setUserId(userId: string): void {
    this._setSync('userId', userId);
    const cur = this._readUserObjectSync();
    if (cur && typeof cur === 'object') {
      this._user$.next({ ...cur, user_id: userId });
    }
  }

  clear(): void {
    localStorage.clear();
    // Note: Preferences.clear() could be added if needed for native, 
    // but preserving original behavior which only cleared localStorage.
  }

  setFixRateBid(data: FixedBidResponse): void {
    this._setSync('fixRateBid', JSON.stringify(data));
  }

  getFixRateBid(): FixedBidResponse | null {
    const raw = this._getSync('fixRateBid');
    if (!raw) return null;
    const data = JSON.parse(raw);
    return Object.keys(data).length === 0 ? null : data;
  }

  clearFixRateBid(): void {
    this._setSync('fixRateBid', '{}');
  }

  async setUnreadNotification(isUnread: boolean): Promise<void> {
    this.cachedUnreadNotification = isUnread;
    await this._set('unreadNotification', isUnread.toString());
  }

  async getUnreadNotification(): Promise<boolean> {
    if (this.cachedUnreadNotification !== null) return this.cachedUnreadNotification;
    const raw = await this._get('unreadNotification');
    this.cachedUnreadNotification = raw === 'true';
    return this.cachedUnreadNotification;
  }

  setAccountChangeRequest(request: string): void {
    this._setSync('accountChangeRequest', request);
  }

  getAccountChangeRequest(): string {
    return this._getSync('accountChangeRequest') || '';
  }

  setPermission(buy: boolean, sell: boolean): void {
    this._setSync('perm', JSON.stringify({ buy, sell }));
  }

  getPermissions(): { buy: boolean; sell: boolean } {
    const raw = this._getSync('perm');
    return raw ? JSON.parse(raw) : { buy: false, sell: false };
  }

  async getUnreadNotificationCount(): Promise<number> {
    const raw = await this._get('unreadNotificationCount');
    return raw ? parseInt(raw, 10) : 0;
  }

  async setUnreadNotificationCount(count: number): Promise<void> {
    await this._set('unreadNotificationCount', count.toString());
  }

  async incrementUnreadNotificationCount(): Promise<number> {
    const current = await this.getUnreadNotificationCount();
    const next = current + 1;
    await this.setUnreadNotificationCount(next);
    return next;
  }

  setBrand(brand: string): void {
    this._setSync('brand', brand);
    this._setSync('hygge_user', brand);
    const cur = this._readUserObjectSync();
    this._user$.next({ ...(cur || {}), name: brand, brand });
  }

  getBrand(): string {
    const b = this._getSync('brand');
    if (!b) return this.getUser();
    return b;
  }

  setLogoUrl(logoUrl: string): void {
    this._setSync('logo_url', logoUrl);
    const cur = this._readUserObjectSync();
    this._user$.next({ ...(cur || {}), logo_url: logoUrl, logoUrl: logoUrl });
  }

  getLogoUrl(): string {
    return this._getSync('logo_url') || '';
  }

  setUtilityId(utilityId: string): void {
    this._setSync('utilityId', utilityId);
    const cur = this._readUserObjectSync();
    this._user$.next({ ...(cur || {}), utilityId });
  }

  getUtilityId(): string {
    return this._getSync('utilityId') || this.getUserId();
  }

  setUserObject(obj: any): void {
    try {
      this._setSync('hygge_user', JSON.stringify(obj));
      if (obj.name) this._setSync('brand', obj.name);
      if (obj.logo_url) this._setSync('logo_url', obj.logo_url);
      if (obj.user_id) this._setSync('userId', obj.user_id);
      this._user$.next(obj);
    } catch { /* ignore */ }
  }

  setUpdatedBid(value: boolean): void {
    this._setSync('updatedBid', value.toString());
  }

  getUpdatedBid(): boolean {
    return this._getSync('updatedBid') === 'true';
  }

  private _readUserObjectSync(): any | null {
    const raw = this._getSync('hygge_user');
    if (!raw) return null;
    return this._safeParse(raw) ?? raw;
  }

  private _safeParse(input: string | null): any | null {
    if (!input) return null;
    try {
      return JSON.parse(input);
    } catch {
      return null;
    }
  }
}
